import { memo, useMemo } from 'react';
import { ConvertType, useFormatNumber } from '../../../hooks/useFormatNumber';
import { T } from '../../../translation/src';
import { StaticColorProperty } from './ColorProperties';
import { PropertiesSection, Property } from './PropertiesSection';
import { useMeasurements } from '../../../hooks/useMeasurements';
import { PropertyPanel } from '../../Properties/components/PropertyPanel';
import { AnnotationType } from '../../Properties/types';
import { ICadLine } from '../../../utils/CADFileParser';
import { useParams } from 'react-router-dom';
import { getDistancePointsProperty } from '@pointorama/potree-shared';
import { usePropertyValue } from '../../Properties/reactive/usePropertyValue';

interface DistancePropertiesProps {
  identifier: string;
}
const DistanceProperties_: React.FC2<DistancePropertiesProps> = ({ identifier }) => {
  const { projectId = '' } = useParams();
  const { formatNumber } = useFormatNumber();
  const { heightMeasurement, slopeDistanceMeasurement } = useMeasurements();

  const points = usePropertyValue(getDistancePointsProperty(identifier));
  const positions = useMemo(() => points.map((p) => p.position), [points]);

  const slopeDistance = slopeDistanceMeasurement(positions);
  const height = heightMeasurement(positions);
  const distance = Math.sqrt((height * height - slopeDistance * slopeDistance) * -1);

  return (
    <div className="flex flex-col space-y-4 divide-y divide-gray-800">
      <PropertiesSection title={<T _str="information" swc />}>
        <Property title={<T _str="type" swc />}>
          <T _str="line" swc />
        </Property>
      </PropertiesSection>
      <PropertiesSection title={<T _str="measurements" swc />}>
        <Property title={<T _str="3D Distance" swc />}>{formatNumber(slopeDistance, { convertType: ConvertType.DISTANCE, addUnit: true })}</Property>
        <Property title={<T _str="2D Distance" swc />}>{formatNumber(distance, { convertType: ConvertType.DISTANCE, addUnit: true })}</Property>
        <Property title={<T _str="Height Difference" swc />}>{formatNumber(height, { convertType: ConvertType.DISTANCE, addUnit: true })}</Property>
      </PropertiesSection>
      <PropertyPanel projectId={projectId} annotationTypes={[AnnotationType.DISTANCE]} objectIdentifiers={[identifier]} />
    </div>
  );
};
export const DistanceProperties = memo(DistanceProperties_);

const CadDistanceProperties_: React.FC2<{ cadLine: ICadLine }> = ({ cadLine }) => {
  const { formatNumber } = useFormatNumber();
  const { heightMeasurement, slopeDistanceMeasurement } = useMeasurements();

  const slopeDistance = slopeDistanceMeasurement(cadLine.points);
  const height = heightMeasurement(cadLine.points);
  const distance = Math.sqrt((height * height - slopeDistance * slopeDistance) * -1);

  return (
    <div className="flex flex-col space-y-4 divide-y divide-gray-800">
      <PropertiesSection title={<T _str="information" swc />}>
        <Property title={<T _str="type" swc />}>
          <T _str="line" swc />
        </Property>
      </PropertiesSection>
      <PropertiesSection title={<T _str="measurements" swc />}>
        <Property title={<T _str="3D Distance" swc />}>{formatNumber(slopeDistance, { convertType: ConvertType.DISTANCE, addUnit: true })}</Property>
        <Property title={<T _str="2D Distance" swc />}>{formatNumber(distance, { convertType: ConvertType.DISTANCE, addUnit: true })}</Property>
        <Property title={<T _str="Height Difference" swc />}>{formatNumber(height, { convertType: ConvertType.DISTANCE, addUnit: true })}</Property>
      </PropertiesSection>
      <PropertiesSection title={<T _str="appearance" swc />}>
        <StaticColorProperty color={cadLine.color} />
      </PropertiesSection>
    </div>
  );
};

export const CadDistanceProperties = memo(CadDistanceProperties_);
